/**
 * Extracts the 11-character YouTube video ID from any recognized URL format:
 *   https://www.youtube.com/watch?v=ID
 *   https://youtu.be/ID
 *   https://youtube.com/shorts/ID
 *   https://www.youtube.com/embed/ID
 * Returns null if no ID is found.
 */
export const extractYouTubeId = (url = "") => {
  if (!url) return null;
  const patterns = [
    /[?&]v=([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
};

/** Returns true for any YouTube URL regardless of format. */
export const isYouTubeUrl = (url = "") =>
  Boolean(url && (url.includes("youtube.com") || url.includes("youtu.be")));

/**
 * Converts any YouTube URL to the canonical embed form (no extra params).
 * Used when normalizing URLs for storage (onSubmit / serGallery).
 * Returns the original string unchanged if no ID can be extracted.
 */
export const normalizeYouTubeUrl = (url = "") => {
  const id = extractYouTubeId(url);
  return id ? `https://www.youtube.com/embed/${id}` : url;
};

/**
 * Builds a YouTube embed URL for a full-screen hero background video.
 * loop=1 + playlist=ID is intentionally removed: that combination puts YouTube
 * into "playlist mode" which renders ◄ ‖ ► navigation buttons even with
 * controls=0. Looping is handled programmatically in ProjectDetailPage via the
 * YouTube IFrame postMessage API (onStateChange → seekTo(0) + playVideo).
 * enablejsapi=1 is required for that postMessage loop to work.
 */
export const buildYouTubeHeroUrl = (url = "") => {
  const id = extractYouTubeId(url);
  if (!id) return normalizeYouTubeUrl(url);
  const p = new URLSearchParams({
    autoplay:        "1",
    mute:            "1",
    controls:        "0",
    playsinline:     "1",
    rel:             "0",
    modestbranding:  "1",
    iv_load_policy:  "3",
    disablekb:       "1",
    enablejsapi:     "1",
  });
  return `https://www.youtube.com/embed/${id}?${p.toString()}`;
};

/**
 * Builds a YouTube embed URL for interactive gallery use.
 * Shows controls; suppresses suggested videos and branding.
 */
export const buildYouTubeGalleryUrl = (url = "") => {
  const id = extractYouTubeId(url);
  if (!id) return normalizeYouTubeUrl(url);
  return `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`;
};
